import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { Collapse } from 'react-collapse';
import {
    createOtherCategoriesByPageSelector,
    otherCategoriesTotalResultsSelector,
} from '@/redux/modules/otherCategories/otherCategories.selectors';
import { debounce } from 'es-toolkit';
import {
    defaultOtherCategoriesOptions,
    defaultOtherCategoriesPageSize,
} from '@/pages/DiscoverCategories/components/DiscoverCategories/components/OtherCategories/constants/otherCategoriesConstants';
import { fetchOtherCategoriesIfNeeded } from '@/redux/modules/otherCategories/otherCategories';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import OtherCategoryCard from '@/pages/DiscoverCategories/components/DiscoverCategories/components/OtherCategories/OtherCategoryCard/OtherCategoryCard';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const OtherCategoriesDropDown = (): ReactElement => {
    const dispatch = useAppDispatch();
    const titleRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultOtherCategoriesPageSize);
    const [currentCategories, setCurrentCategories] = useState([]);
    const categories = useAppSelector(createOtherCategoriesByPageSelector(pageNumber, pageSize));
    const totalResults = useAppSelector(otherCategoriesTotalResultsSelector);

    useEffect(() => {
        dispatch(fetchOtherCategoriesIfNeeded(pageNumber, pageSize));
    }, [dispatch, pageNumber, pageSize]);

    useEffect(() => {
        if (categories) {
            setCurrentCategories(categories);
        }
    }, [categories]);

    const onPageChange = ({ page: newPageNumber, pageSize: newPageSize }) => {
        setPageNumber(newPageNumber);
        if (newPageSize) {
            setPageSize(newPageSize);
        }
        if (titleRef?.current?.scrollIntoView) {
            titleRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <section>
            <HeaderWrapper onClick={toggleOpen}>
                <HeaderTitle ref={titleRef}>
                    <FormattedMessage id="discoverPage.otherCategories.title" />
                </HeaderTitle>
                <CaretArrowContainer>
                    {isOpen ? (
                        <CaretUp
                            data-testid="up-arrow"
                            size="xs"
                            style="fill"
                        />
                    ) : (
                        <CaretDown
                            data-testid="down-arrow"
                            size="xs"
                            style="fill"
                        />
                    )}
                </CaretArrowContainer>
            </HeaderWrapper>
            <Collapse isOpened={isOpen}>
                <StyledDropDown>
                    <Pagination
                        // The wait is needed for the page number links. It seems that clicking a link is causing the page to "reload"
                        // Because of this, the scrolling behavior sometimes gets stuck. To address this, we add a debounce to wait 200ms before scrolling upwards
                        onChange={debounce(onPageChange, 200)}
                        pageSizeOptions={defaultOtherCategoriesOptions}
                        paginationFilter={{
                            page: pageNumber,
                            pageSize,
                            totalRecords: totalResults,
                        }}
                    >
                        <StyledCardsBox>
                            {currentCategories.map((category) => (
                                <OtherCategoryCard
                                    category={category}
                                    key={category.title}
                                />
                            ))}
                        </StyledCardsBox>
                    </Pagination>
                </StyledDropDown>
            </Collapse>
            <LineSeperator $isOpen={isOpen} />
        </section>
    );
};

export default OtherCategoriesDropDown;

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const HeaderTitle = styled(H2)`
    margin: 24px 0;
`;

const CaretArrowContainer = styled.div`
    display: flex;
    align-items: center;
`;

const StyledDropDown = styled.div`
    [class^='paginationWrapper'] {
        width: 50%;
        margin: auto auto;
    }

    [class^='paginator'] {
        margin-top: 32px;
    }
`;

const StyledCardsBox = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const LineSeperator = styled.div<{ $isOpen: boolean }>`
    margin-top: ${({ $isOpen }) => ($isOpen ? '32px' : 0)};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
`;
